import { useState, useEffect } from "react";
import { trackEvent } from "@/lib/analytics";
import { Link, useLocation, useSearch } from "wouter";
import StaffLayout from "@/components/StaffLayout";
import {
  useListReports,
  useUpdateReport,
  useCreateComment,
  useGetReport,
  getGetReportQueryKey,
  getListReportsQueryKey,
  getListReportAuditLogsQueryKey,
  useLinkReportDuplicate,
  useUnlinkReportDuplicate,
  useListReportAuditLogs,
  AuditLog,
  useListCategories,
  useListStatuses,
  Status,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import {
  MessageSquare,
  ShieldAlert,
  User,
  Search,
  X,
  ClipboardList,
  Clock,
  UserCheck,
  Paperclip,
  Phone,
  PhoneCall,
  Tag,
  Link2,
  Link2Off,
  Users,
  Wrench,
} from "lucide-react";
import AttachmentList from "@/components/AttachmentList";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@clerk/react";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// After the issue/report unification every audit row uses 'issue.*'. We still
// honor legacy 'report.*' rows in the DB so historical timelines keep rendering.
const ACTION_LABELS: Record<string, string> = {
  "issue.create": "Report submitted",
  "issue.update": "Report updated",
  "issue.delete": "Report deleted",
  "report.create": "Report submitted",
  "report.update": "Report updated",
  "report.delete": "Report deleted",
  "comment.create": "Comment added",
  "comment.delete": "Comment deleted",
};

const ACTION_DOTS: Record<string, string> = {
  "issue.create": "bg-blue-400",
  "issue.update": "bg-amber-400",
  "issue.delete": "bg-red-400",
  "report.create": "bg-blue-400",
  "report.update": "bg-amber-400",
  "report.delete": "bg-red-400",
  "comment.create": "bg-emerald-400",
  "comment.delete": "bg-red-400",
};

type ResolveDiff = (field: string, value: unknown) => string;

function ActivityTimeline({ logs, resolveDiff }: { logs: AuditLog[]; resolveDiff: ResolveDiff }) {
  if (logs.length === 0) {
    return (
      <p className="text-sm text-slate-500 text-center py-4">
        No activity recorded yet.
      </p>
    );
  }

  return (
    <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-4">
      {logs.map((log) => (
        <li key={log.id} className="ml-4">
          <div
            className={`absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${
              ACTION_DOTS[log.actionType] ?? "bg-slate-400"
            }`}
          />
          <div className="flex flex-col">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
              {ACTION_LABELS[log.actionType] ?? log.actionType}
              {log.actorName && (
                <span className="font-normal text-slate-500"> by {log.actorName}</span>
              )}
            </span>
            <span className="text-[10px] text-slate-400">
              {format(new Date(log.createdAt), "MMM d, yyyy 'at' p")}
            </span>
            {log.diff &&
              typeof log.diff === "object" &&
              Object.keys(log.diff).length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {Object.entries(
                    log.diff as Record<string, { before: unknown; after: unknown }>,
                  ).map(([field, change]) => (
                    <div
                      key={field}
                      className="text-[10px] font-mono text-slate-500"
                    >
                      <span className="capitalize">{field}</span>:{" "}
                      <span className="text-red-500 line-through">
                        {resolveDiff(field, change.before)}
                      </span>{" "}
                      →{" "}
                      <span className="text-emerald-600">
                        {resolveDiff(field, change.after)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function MetadataRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}:</span>
      <span className="font-medium text-right max-w-[60%] truncate" title={value}>
        {value}
      </span>
    </div>
  );
}

export default function StaffDashboardPage() {
  const [searchInput, setSearchInput] = useState("");
  const [usdotInput, setUsdotInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedUsdot = useDebounce(usdotInput, 300);

  const params = {
    ...(statusFilter && statusFilter !== "all"
      ? { statusId: Number(statusFilter) }
      : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(debouncedUsdot ? { usdot: debouncedUsdot } : {}),
    ...(categoryFilter && categoryFilter !== "all"
      ? { categoryId: Number(categoryFilter) }
      : {}),
  };

  const { data: reports, isLoading } = useListReports(params);
  const { data: categories } = useListCategories();
  const { data: reportStatuses } = useListStatuses({ scope: "reports" });
  const statusById = new Map<number, Status>((reportStatuses ?? []).map((s) => [s.id, s]));
  const categoryById = new Map((categories ?? []).map((c) => [c.id, c]));
  const categoryNameFor = (id: number | null | undefined) =>
    id != null ? categoryById.get(id)?.name : undefined;
  const activeReportStatuses = (reportStatuses ?? []).filter((s) => s.isActive);

  // Translate raw FK ids into human labels when rendering audit diffs.
  const resolveDiff: ResolveDiff = (field, value) => {
    if (value == null) return "(none)";
    if (field === "statusId") {
      const n = Number(value);
      return Number.isFinite(n) ? (statusById.get(n)?.label ?? String(value)) : String(value);
    }
    if (field === "categoryId") {
      const n = Number(value);
      return Number.isFinite(n) ? (categoryById.get(n)?.name ?? String(value)) : String(value);
    }
    return String(value);
  };
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  // Deep link from staff alert emails: `/staff?report=<id>` opens that
  // report's detail sheet on arrival (see internalAlerts.ts staffReportLink).
  const search = useSearch();
  const [location, navigate] = useLocation();
  useEffect(() => {
    const raw = new URLSearchParams(search).get("report");
    // Whole numbers only: parseInt would read "55abc" as report 55.
    if (!raw || !/^\d+$/.test(raw)) return;
    const id = Number(raw);
    if (id > 0) setSelectedReportId(id);
  }, [search]);

  // Closing the sheet also drops ?report= from the URL, so a refresh or a
  // remount does not reopen a report the user already dismissed.
  const closeReport = () => {
    setSelectedReportId(null);
    const params = new URLSearchParams(search);
    if (!params.has("report")) return;
    params.delete("report");
    const rest = params.toString();
    navigate(rest ? `${location}?${rest}` : location, { replace: true });
  };
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Every report mutation invalidates the same three generated keys: the
  // list, the detail, and the audit log. Attached once at hook level, keyed
  // by the mutation's own `vars.id`, so per-call callbacks only carry UI
  // concerns (toasts, analytics).
  const invalidateReport = (id: number) => {
    queryClient.invalidateQueries({ queryKey: getListReportsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetReportQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListReportAuditLogsQueryKey(id) });
  };

  const updateReport = useUpdateReport({
    mutation: { onSuccess: (_, vars) => invalidateReport(vars.id) },
  });
  const createComment = useCreateComment({
    mutation: { onSuccess: (_, vars) => invalidateReport(vars.id) },
  });
  const linkDuplicate = useLinkReportDuplicate({
    mutation: { onSuccess: (_, vars) => invalidateReport(vars.id) },
  });
  const unlinkDuplicate = useUnlinkReportDuplicate({
    mutation: { onSuccess: (_, vars) => invalidateReport(vars.id) },
  });

  const {
    data: reportDetails,
    isLoading: reportDetailsLoading,
    isError: reportDetailsIsError,
    error: reportDetailsError,
    refetch: refetchReportDetails,
  } = useGetReport(selectedReportId as number, {
    query: {
      enabled: !!selectedReportId,
      queryKey: getGetReportQueryKey(selectedReportId as number),
    },
  });

  const reportDetailsStatus =
    reportDetailsError && typeof reportDetailsError === "object"
      ? (reportDetailsError as { status?: number }).status
      : undefined;

  const { data: auditLogs, isLoading: auditLogsLoading } =
    useListReportAuditLogs(selectedReportId as number, {
      query: {
        enabled: !!selectedReportId,
        queryKey: getListReportAuditLogsQueryKey(selectedReportId as number),
      },
    });

  const [commentBody, setCommentBody] = useState("");

  const handleStatusChange = (newStatusId: number) => {
    if (!selectedReportId) return;

    updateReport.mutate(
      { id: selectedReportId, data: { statusId: newStatusId } },
      {
        onSuccess: () => {
          trackEvent("report_status_changed", {
            new_status: statusById.get(newStatusId)?.label ?? String(newStatusId),
          });
          toast({ title: "Status updated" });
        },
      },
    );
  };

  const handleVerifyToggle = (verified: boolean) => {
    if (!selectedReportId) return;
    updateReport.mutate(
      { id: selectedReportId, data: { verified } },
      {
        onSuccess: () => {
          toast({
            title: verified ? "Marked as verified" : "Marked as unverified",
          });
        },
      },
    );
  };

  const handleWorkaroundToggle = (hasWorkaround: boolean) => {
    if (!selectedReportId) return;
    updateReport.mutate(
      { id: selectedReportId, data: { hasWorkaround } },
      {
        onSuccess: () => {
          toast({
            title: hasWorkaround
              ? "Marked: workaround available"
              : "Removed workaround flag",
          });
        },
      },
    );
  };

  const handleCategoryChange = (newCategory: string) => {
    if (!selectedReportId) return;
    const categoryValue = newCategory === "__none__" ? null : Number(newCategory);

    updateReport.mutate(
      { id: selectedReportId, data: { categoryId: categoryValue } },
      {
        onSuccess: () => {
          toast({ title: "Category updated" });
        },
      },
    );
  };

  const handleLinkDuplicate = (primaryId: number) => {
    if (!selectedReportId) return;
    linkDuplicate.mutate(
      { id: selectedReportId, data: { primaryId } },
      {
        onSuccess: () => {
          toast({ title: `Linked as a duplicate of #${primaryId}` });
        },
        onError: () => {
          toast({
            title: "Couldn't link this report",
            description: "It may already have duplicates, or the chosen issue is itself a duplicate.",
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleUnlinkDuplicate = () => {
    if (!selectedReportId) return;
    unlinkDuplicate.mutate(
      { id: selectedReportId },
      {
        onSuccess: () => {
          toast({ title: "Unlinked from primary issue" });
        },
        onError: () => {
          toast({ title: "Couldn't unlink this report", variant: "destructive" });
        },
      },
    );
  };

  const handleAddComment = () => {
    if (!selectedReportId || !commentBody.trim() || !user) return;

    createComment.mutate(
      {
        id: selectedReportId,
        data: {
          body: commentBody,
          authorName:
            user.fullName || user.primaryEmailAddress?.emailAddress || "Staff",
        },
      },
      {
        onSuccess: () => {
          trackEvent("comment_added");
          setCommentBody("");
          toast({ title: reportDetails?.submitterEmail == null ? "Public update saved" : "Private reply saved" });
        },
      },
    );
  };

  const clearFilters = () => {
    setSearchInput("");
    setUsdotInput("");
    setStatusFilter("all");
    setCategoryFilter("all");
  };

  const hasActiveFilters =
    searchInput || usdotInput || (statusFilter && statusFilter !== "all") || (categoryFilter && categoryFilter !== "all");

  const getStatusBadge = (statusId: number) => {
    const status = statusById.get(statusId);
    if (!status) return <Badge variant="secondary">—</Badge>;
    switch (status.key) {
      case "new":
        return <Badge variant="secondary">{status.label}</Badge>;
      case "in_review":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300">
            {status.label}
          </Badge>
        );
      case "resolved":
        return (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-300">
            {status.label}
          </Badge>
        );
      default:
        return <Badge variant="outline">{status.label}</Badge>;
    }
  };

  return (
    <StaffLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900 dark:text-white"
            data-testid="staff-dashboard-title"
          >
            Reports
          </h1>
          <p className="text-slate-500 text-sm">
            Every carrier submission and staff-logged report in one queue. Review, edit, archive, or reply.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link href="/staff/audit-log">
              <ClipboardList className="w-4 h-4 mr-2" /> Audit Log
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            data-testid="search-input"
            placeholder="Search by name or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="relative sm:w-44">
          <Input
            data-testid="usdot-input"
            placeholder="Filter by USDOT #"
            value={usdotInput}
            onChange={(e) => setUsdotInput(e.target.value)}
            className="font-mono"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-40" data-testid="status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(reportStatuses ?? []).map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {s.label}
                {!s.isActive && " (inactive)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {categories && categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="sm:w-44" data-testid="category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearFilters}
            className="shrink-0"
            title="Clear filters"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Card className="border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
                <TableRow>
                  <TableHead>Submitter</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>DOT #</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Comments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-slate-500"
                    >
                      {hasActiveFilters
                        ? "No reports match your filters."
                        : "No reports found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  reports?.map((report) => (
                    <TableRow
                      key={report.id}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      onClick={() => setSelectedReportId(report.id)}
                      data-testid={`row-report-${report.id}`}
                    >
                      <TableCell>
                        <div className="font-medium text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                          {report.submitterName}
                          {report.createdById && (
                            <span
                              title={`Submitted internally by ${report.createdByName ?? "staff"}`}
                              className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
                              data-testid={`badge-staff-${report.id}`}
                            >
                              <UserCheck className="w-3 h-3" /> {report.createdByName ?? "Staff"}
                            </span>
                          )}
                          {report.requestExpert && (
                            <span
                              title="Wants to speak with an expert"
                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
                              data-testid={`badge-expert-${report.id}`}
                            >
                              <PhoneCall className="w-3 h-3" /> Expert
                            </span>
                          )}
                          {report.duplicateOfId != null && (
                            <span
                              title={`Linked as a duplicate of #${report.duplicateOfId}`}
                              className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
                              data-testid={`badge-duplicate-${report.id}`}
                            >
                              <Link2 className="w-3 h-3" /> Duplicate of #{report.duplicateOfId}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {report.submitterEmail}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-900 dark:text-white max-w-[200px]">
                        <button
                          className="line-clamp-1 text-left hover:underline hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); setSelectedReportId(report.id); }}
                        >
                          {report.title}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {report.dotNumber}
                      </TableCell>
                      <TableCell>
                        {report.categoryId != null && categoryNameFor(report.categoryId) ? (
                          <Badge variant="outline" className="text-xs font-normal">
                            {categoryNameFor(report.categoryId)}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(report.statusId)}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {format(new Date(report.createdAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="bg-slate-50">
                          <MessageSquare className="w-3 h-3 mr-1" />{" "}
                          {report.commentCount || 0}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Detail Sheet */}
      <Sheet
        open={!!selectedReportId}
        onOpenChange={(open) => {
          if (!open) closeReport();
        }}
      >
        <SheetContent className="w-full sm:max-w-md md:max-w-lg overflow-y-auto flex flex-col">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle>Report Details</SheetTitle>
            <SheetDescription>
              View issue details and communicate with the submitter.
            </SheetDescription>
            {reportDetails && (
              <div className="flex items-center gap-2 flex-wrap pt-1">
                {getStatusBadge(reportDetails.statusId)}
                {reportDetails.hasWorkaround && (
                  <Badge
                    className="bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border-transparent flex items-center gap-1 text-xs font-medium"
                    data-testid="badge-workaround-detail"
                  >
                    <Wrench className="w-3 h-3" /> Workaround available
                  </Badge>
                )}
                {reportDetails.submitterType && (
                  <Badge variant="outline" className="text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-medium capitalize">
                    {reportDetails.submitterType.replace("_", " ")}
                  </Badge>
                )}
                {reportDetails.createdById && (
                  <Badge
                    className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border-transparent flex items-center gap-1 text-xs font-medium"
                    data-testid="badge-staff-detail"
                  >
                    <UserCheck className="w-3 h-3" /> Internal / Staff
                  </Badge>
                )}
                {reportDetails.categoryId != null && categoryNameFor(reportDetails.categoryId) ? (
                  <Badge variant="outline" className="text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 flex items-center gap-1 text-xs font-medium">
                    <Tag className="w-3 h-3" />
                    {categoryNameFor(reportDetails.categoryId)}
                  </Badge>
                ) : (
                  <span className="text-xs text-slate-400 italic">No category</span>
                )}
              </div>
            )}
          </SheetHeader>

          {reportDetailsLoading ? (
            <div className="py-8 space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : reportDetailsIsError || !reportDetails ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12 px-6 gap-3">
              <ShieldAlert className="w-10 h-10 text-amber-500" />
              <div className="font-medium">
                {reportDetailsStatus === 401 || reportDetailsStatus === 403
                  ? "Your staff session has expired"
                  : "Couldn't load this report"}
              </div>
              <p className="text-sm text-slate-500 max-w-xs">
                {reportDetailsStatus === 401 || reportDetailsStatus === 403
                  ? "Please sign in again to view report details."
                  : "Something went wrong while loading the details. Please try again."}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchReportDetails()}
                data-testid="button-retry-report-details"
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto py-6 space-y-6">
              {/* Verify control — graduates an unverified public submission
                  into a curated known issue (or demotes one back). */}
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                <div className="text-sm">
                  <div className="font-medium">Verification</div>
                  <div className="text-xs text-slate-500">
                    {reportDetails.verified
                      ? "Reviewed by staff."
                      : "Not yet reviewed by staff."}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={reportDetails.verified ? "outline" : "default"}
                  className={reportDetails.verified ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                  onClick={() => handleVerifyToggle(!reportDetails.verified)}
                  disabled={updateReport.isPending}
                  data-testid="button-toggle-verify"
                >
                  {reportDetails.verified ? "Mark unverified" : "Verify"}
                </Button>
              </div>

              {/* Workaround control — flags that a workaround exists for this
                  issue; shown as a badge on the public board and dashboard. */}
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                <div className="text-sm">
                  <div className="font-medium">Workaround</div>
                  <div className="text-xs text-slate-500">
                    {reportDetails.hasWorkaround
                      ? "A workaround is available — shows a badge on the public board."
                      : "No workaround flagged for this issue."}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={reportDetails.hasWorkaround ? "outline" : "default"}
                  className={reportDetails.hasWorkaround ? "" : "bg-sky-600 hover:bg-sky-700"}
                  onClick={() => handleWorkaroundToggle(!reportDetails.hasWorkaround)}
                  disabled={updateReport.isPending}
                  data-testid="button-toggle-workaround"
                >
                  {reportDetails.hasWorkaround ? "Remove workaround" : "Mark workaround"}
                </Button>
              </div>

              {/* Status control */}
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                <span className="text-sm font-medium">Status</span>
                <Select
                  value={String(reportDetails.statusId)}
                  onValueChange={(val) => handleStatusChange(Number(val))}
                  disabled={updateReport.isPending}
                >
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {activeReportStatuses.length === 0 &&
                      reportStatuses?.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.label}
                        </SelectItem>
                      ))}
                    {activeReportStatuses.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.label}
                      </SelectItem>
                    ))}
                    {/* Keep current status selectable even if it's been deactivated */}
                    {!statusById.get(reportDetails.statusId)?.isActive &&
                      statusById.get(reportDetails.statusId) && (
                        <SelectItem value={String(reportDetails.statusId)}>
                          {statusById.get(reportDetails.statusId)!.label} (current, inactive)
                        </SelectItem>
                      )}
                  </SelectContent>
                </Select>
              </div>

              {/* Category control */}
              {categories && categories.length > 0 && (
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                  <span className="text-sm font-medium">Category</span>
                  <Select
                    value={reportDetails.categoryId != null ? String(reportDetails.categoryId) : "__none__"}
                    onValueChange={handleCategoryChange}
                    disabled={updateReport.isPending}
                  >
                    <SelectTrigger className="w-[160px] h-8 text-xs">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {categories.filter((c) => c.isActive).map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Duplicate / related linking. Keeps the relationship one level
                  deep: a duplicate can't itself have duplicates, and a primary
                  can't be linked under another issue. */}
              {reportDetails.duplicateOfId != null ? (
                <div className="flex items-start justify-between gap-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 px-4 py-3">
                  <div className="text-sm">
                    <div className="font-medium text-violet-800 dark:text-violet-300 flex items-center gap-1.5">
                      <Link2 className="w-4 h-4" /> Duplicate
                    </div>
                    <div className="text-xs text-violet-700 dark:text-violet-400">
                      Hidden from the public board. Rolled up under issue #{reportDetails.duplicateOfId}.
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleUnlinkDuplicate}
                    disabled={unlinkDuplicate.isPending}
                    data-testid="button-unlink-duplicate"
                  >
                    <Link2Off className="w-3.5 h-3.5 mr-1" /> Unlink
                  </Button>
                </div>
              ) : reportDetails.linkedDuplicates && reportDetails.linkedDuplicates.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-4 py-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-slate-400" /> Primary issue
                    </span>
                    {reportDetails.rolledUpAffectedCount != null && (
                      <Badge variant="secondary" className="text-xs">
                        {reportDetails.rolledUpAffectedCount} affected (rolled up)
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    {reportDetails.linkedDuplicates.length} duplicate
                    {reportDetails.linkedDuplicates.length === 1 ? "" : "s"} grouped under this issue.
                  </p>
                  <ul className="space-y-1">
                    {reportDetails.linkedDuplicates.map((dup) => (
                      <li
                        key={dup.id}
                        className="flex items-center justify-between gap-2 text-xs bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 px-2 py-1.5"
                        data-testid={`linked-duplicate-${dup.id}`}
                      >
                        <button
                          className="truncate text-left hover:underline text-slate-700 dark:text-slate-200"
                          onClick={() => setSelectedReportId(dup.id)}
                          title={dup.title ?? dup.description}
                        >
                          #{dup.id} {dup.title ?? dup.description}
                        </button>
                        {dup.affectedCount != null && (
                          <span className="shrink-0 text-slate-400">{dup.affectedCount}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Link2 className="w-4 h-4 text-slate-400" /> Link as duplicate
                  </span>
                  <Select
                    value=""
                    onValueChange={(val) => handleLinkDuplicate(Number(val))}
                    disabled={linkDuplicate.isPending}
                  >
                    <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="select-link-duplicate">
                      <SelectValue placeholder="Pick a primary…" />
                    </SelectTrigger>
                    <SelectContent>
                      {reports
                        ?.filter(
                          (r) =>
                            r.id !== selectedReportId && r.duplicateOfId == null,
                        )
                        .map((r) => (
                          <SelectItem key={r.id} value={String(r.id)}>
                            #{r.id} {r.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Expert callback banner */}
              {reportDetails.requestExpert && (
                <div
                  className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3"
                  data-testid="banner-request-expert"
                >
                  <PhoneCall className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                      Wants to speak with an expert: YES
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      {reportDetails.phoneNumber ? (
                        <>Call them at <strong>{reportDetails.phoneNumber}</strong>.</>
                      ) : (
                        <>No phone given — email them at <strong>{reportDetails.submitterEmail}</strong>.</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Submitter info */}
              <div className="space-y-1">
                <MetadataRow
                  label="Submitter"
                  value={reportDetails.submitterName}
                />
                <MetadataRow label="Email" value={reportDetails.submitterEmail} />
                {reportDetails.phoneNumber && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" /> Phone:
                    </span>
                    <span className="font-medium">{reportDetails.phoneNumber}</span>
                  </div>
                )}
                <MetadataRow
                  label="DOT Number"
                  value={reportDetails.dotNumber}
                />
                {reportDetails.otherCompanyType && (
                  <MetadataRow
                    label="Company type"
                    value={reportDetails.otherCompanyType}
                  />
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Submitted:</span>
                  <span>
                    {format(new Date(reportDetails.createdAt), "PPp")}
                  </span>
                </div>
                {reportDetails.createdByName && (
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-slate-500 flex items-center gap-1">
                      <UserCheck className="w-3.5 h-3.5" /> Submitted by (staff):
                    </span>
                    <span className="font-medium">
                      {reportDetails.createdByName}
                    </span>
                  </div>
                )}
                {reportDetails.lastModifiedByName && (
                  <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-slate-500 flex items-center gap-1">
                        <UserCheck className="w-3.5 h-3.5" /> Last modified by:
                      </span>
                      <span className="font-medium">
                        {reportDetails.lastModifiedByName}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> Last modified:
                      </span>
                      <span>
                        {format(new Date(reportDetails.updatedAt), "PPp")}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h4 className="text-sm font-bold mb-2">Description</h4>
                <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap border border-slate-200 dark:border-slate-800">
                  {reportDetails.description}
                </div>
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <h4 className="text-sm font-bold flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-slate-400" />
                  Attachments
                </h4>
                <AttachmentList reportId={selectedReportId as number} compact />
              </div>

              {/* Comments */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold flex items-center justify-between border-b pb-2">
                  <span>Comments Thread</span>
                  <Badge variant="secondary">
                    {reportDetails.comments?.length || 0}
                  </Badge>
                </h4>

                <div className="space-y-4">
                  {reportDetails.comments?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No comments yet.
                    </p>
                  ) : (
                    reportDetails.comments?.map((comment: any) => (
                      <div
                        key={comment.id}
                        className={`p-3 rounded-lg text-sm ${
                          comment.isStaff
                            ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50"
                            : "bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800"
                        }`}
                      >
                        <div className="flex justify-between mb-1">
                          <span className="font-semibold text-xs flex items-center gap-1">
                            {comment.isStaff ? (
                              <ShieldAlert className="w-3 h-3 text-amber-600" />
                            ) : (
                              <User className="w-3 h-3 text-slate-400" />
                            )}
                            {comment.authorName}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {format(new Date(comment.createdAt), "MMM d, p")}
                          </span>
                        </div>
                        <p className="text-slate-700 dark:text-slate-300">
                          {comment.body}
                        </p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Comment Form */}
                <div className="pt-4 space-y-3">
                  <div
                    className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                    id="comment-visibility"
                    data-testid={reportDetails.submitterEmail == null ? "notice-comment-public" : "notice-comment-private"}
                  >
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      {reportDetails.submitterEmail == null
                        ? 'Public update: visible on the Issues board as "DNA Staff". No recipient email is available, so a private reply cannot be sent.'
                        : `Private reply to ${reportDetails.submitterEmail}: visible to the submitter in My Reports, not on the public board. The app also attempts to email them a copy.`}
                    </span>
                  </div>
                  <Textarea
                    data-testid="textarea-comment"
                    aria-label={reportDetails.submitterEmail == null ? "Public update" : "Private reply to submitter"}
                    aria-describedby="comment-visibility"
                    placeholder={reportDetails.submitterEmail == null ? "Write a public update..." : "Write a private reply to the submitter..."}
                    className="min-h-[100px] text-sm"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button
                      data-testid="button-add-comment"
                      size="sm"
                      onClick={handleAddComment}
                      disabled={!commentBody.trim() || createComment.isPending}
                      className="bg-primary hover:bg-orange-600"
                    >
                      {createComment.isPending
                        ? "Saving..."
                        : reportDetails.submitterEmail == null ? "Post public update" : "Send private reply"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold flex items-center gap-2 border-b pb-2">
                  <ClipboardList className="w-4 h-4 text-slate-400" />
                  Activity Timeline
                </h4>
                {auditLogsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <ActivityTimeline logs={auditLogs ?? []} resolveDiff={resolveDiff} />
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </StaffLayout>
  );
}
